'use client';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, User, LogIn, Settings, LogOut, UserPlus, Bell } from 'lucide-react';
import { fetchMyNotifications, markNotificationRead, markAllNotificationsRead, getNotificationIcon, Notification } from '@/lib/supabase/notifications';

// Primary nav — always visible on desktop
const primaryNavItems = [
  { href: '/', labelEN: 'Home', labelZH: '首页' },
  { href: '/weekly-plan', labelEN: 'Weekly Plan', labelZH: '训练计划' },
  { href: '/community', labelEN: 'Community', labelZH: '社群' },
];

interface HeaderProps {
  isLoggedIn?: boolean;
  onLoginClick?: () => void;
  onShowDiagnostics?: () => void;
  onShowChecklist?: () => void;
}

// ─── Notification Bell Component ──────────────────────────────────────────────
function NotificationBell({ isZH }: { isZH: boolean }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const unreadCount = notifications.filter((n) => n.status === 'unread').length;

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await fetchMyNotifications(20);
      setNotifications(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    // Poll every 60 seconds
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleMarkRead = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await markNotificationRead(id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, status: 'read', read_at: new Date().toISOString() } : n))
    );
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, status: 'read', read_at: n.read_at || new Date().toISOString() })));
  };

  if (!mounted) return null;

  return (
    <div ref={bellRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, borderRadius: 2,
          border: '1.5px solid #D8D8CE',
          backgroundColor: open ? '#B8FF00' : 'transparent',
          cursor: 'pointer', transition: 'all 0.15s ease',
        }}
        aria-label="Notifications"
      >
        <Bell size={14} color="#070707" />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            minWidth: 16, height: 16,
            backgroundColor: '#FF3B1F', color: '#fff',
            borderRadius: '50%', fontSize: 9, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 3px',
            fontFamily: 'var(--font-mono, monospace)',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 8,
          width: 320, maxHeight: 420,
          backgroundColor: '#F7F7F2',
          border: '1.5px solid #111111',
          borderRadius: 4,
          boxShadow: '0 8px 32px rgba(7,7,7,0.12)',
          zIndex: 200,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid #D8D8CE', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Bell size={12} color="#5F625F" />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#070707', fontFamily: 'var(--font-mono, monospace)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {isZH ? '通知' : 'Notifications'}
              </span>
              {unreadCount > 0 && (
                <span style={{ fontSize: 9, fontWeight: 700, backgroundColor: '#FF3B1F', color: '#fff', borderRadius: 10, padding: '1px 5px' }}>
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{ fontSize: 10, fontWeight: 600, color: '#5F625F', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                {isZH ? '全部已读' : 'Mark all read'}
              </button>
            )}
          </div>

          {/* Notification list */}
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: 300 }}>
            {loading && notifications.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#5F625F', fontSize: 12 }}>
                {isZH ? '加载中…' : 'Loading…'}
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center' }}>
                <Bell size={24} color="#D8D8CE" style={{ margin: '0 auto 8px' }} />
                <p style={{ fontSize: 12, color: '#5F625F' }}>{isZH ? '暂无通知' : 'No notifications yet'}</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((n) => (
                <div
                  key={n.id}
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid #D8D8CE',
                    backgroundColor: n.status === 'unread' ? '#EDFFD0' : 'transparent',
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                  }}
                >
                  <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{getNotificationIcon(n.type)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#070707', margin: 0, lineHeight: 1.3 }}>{n.title}</p>
                    <p style={{ fontSize: 10, color: '#5F625F', margin: '2px 0 0', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.message}</p>
                  </div>
                  {n.status === 'unread' && (
                    <button
                      onClick={(e) => handleMarkRead(n.id, e)}
                      style={{ fontSize: 9, fontWeight: 600, color: '#5F625F', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 2, flexShrink: 0 }}
                    >
                      ✓
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '10px 14px', borderTop: '1px solid #D8D8CE' }}>
            <Link
              href="/notifications"
              onClick={() => setOpen(false)}
              style={{ display: 'block', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#070707', textDecoration: 'none', fontFamily: 'var(--font-mono, monospace)', textTransform: 'uppercase', letterSpacing: '0.06em' }}
            >
              {isZH ? '查看全部通知 →' : 'View all notifications →'}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Header({ isLoggedIn: isLoggedInProp, onLoginClick, onShowDiagnostics, onShowChecklist }: HeaderProps) {
  const { language, toggleLanguage, setLanguage, t } = useLanguage();
  const { user, isAdmin, loading: authLoading, profileLoading, signOut } = useAuth();
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [portalRoot, setPortalRoot] = useState<Element | null>(null);

  useEffect(() => {
    setMounted(true);
    setPortalRoot(document.body);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    if (moreOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [moreOpen]);

  useEffect(() => { setMobileOpen(false); setMoreOpen(false); }, [pathname]);

  useEffect(() => {
    if (!mounted) return;
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen, mounted]);

  // Auth state — only resolve after mount + auth AND profile loaded
  const authResolved = mounted && !authLoading && !profileLoading;
  const isLoggedIn = authResolved ? (!!user || !!isLoggedInProp) : !!isLoggedInProp;
  const showAdmin = authResolved && isAdmin;
  const isZH = language === 'ZH';

  const isActive = (href: string) => href === '/' ? pathname === '/' : pathname.startsWith(href);

  // ── Mobile Drawer ──────────────────────────────────────────────────────────
  const mobileDrawer = mounted && portalRoot && mobileOpen
    ? ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, top: '56px', zIndex: 200, backgroundColor: 'rgba(7,7,7,0.6)' }}
          onClick={() => setMobileOpen(false)}
        >
          <div
            style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              backgroundColor: '#F7F7F2',
              maxHeight: 'calc(100vh - 56px)',
              display: 'flex', flexDirection: 'column',
              borderBottom: '2px solid #111111',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '8px 16px 20px' }}>

              {/* ── MAIN ── */}
              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#5F625F', padding: '12px 8px 6px', fontFamily: 'var(--font-mono, monospace)' }}>
                {t('Main', '主菜单')}
              </p>
              {[
                { href: '/', labelEN: 'Home', labelZH: '首页' },
                { href: '/weekly-plan', labelEN: 'Weekly Plan', labelZH: '训练计划' },
                { href: '/events', labelEN: 'Events', labelZH: '活动' },
                { href: '/journal', labelEN: 'Journal', labelZH: '日志' },
                { href: '/race-calendar', labelEN: 'Races', labelZH: '赛事' },
                { href: '/community', labelEN: 'Community', labelZH: '社群' },
              ].map((item) => (
                <Link
                  key={`mob-main-${item.href}`}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  style={{
                    display: 'block', padding: '10px 8px', borderRadius: 2,
                    fontSize: 14, fontWeight: isActive(item.href) ? 700 : 500,
                    textDecoration: 'none', minHeight: 42,
                    color: isActive(item.href) ? '#070707' : '#5F625F',
                    backgroundColor: isActive(item.href) ? '#B8FF00' : 'transparent',
                    borderLeft: isActive(item.href) ? '3px solid #070707' : '3px solid transparent',
                    paddingLeft: 10,
                  }}
                >
                  {t(item.labelEN, item.labelZH)}
                </Link>
              ))}

              {/* ── MEMBER (logged in) ── */}
              {isLoggedIn && (
                <>
                  <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#5F625F', padding: '12px 8px 6px', marginTop: 4, borderTop: '1px solid #D8D8CE', fontFamily: 'var(--font-mono, monospace)' }}>
                    {t('Member', '会员')}
                  </p>
                  <Link
                    href="/my-dashboard"
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: 'block', padding: '10px 8px', borderRadius: 2,
                      fontSize: 14, fontWeight: isActive('/my-dashboard') ? 700 : 500,
                      textDecoration: 'none', minHeight: 42,
                      color: isActive('/my-dashboard') ? '#070707' : '#5F625F',
                      backgroundColor: isActive('/my-dashboard') ? '#B8FF00' : 'transparent',
                      borderLeft: isActive('/my-dashboard') ? '3px solid #070707' : '3px solid transparent',
                      paddingLeft: 10,
                    }}
                  >
                    {t('Dashboard', '仪表板')}
                  </Link>
                  <Link
                    href="/notifications"
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: 'block', padding: '10px 8px', borderRadius: 2,
                      fontSize: 14, fontWeight: isActive('/notifications') ? 700 : 500,
                      textDecoration: 'none', minHeight: 42,
                      color: isActive('/notifications') ? '#070707' : '#5F625F',
                      backgroundColor: isActive('/notifications') ? '#B8FF00' : 'transparent',
                      borderLeft: isActive('/notifications') ? '3px solid #070707' : '3px solid transparent',
                      paddingLeft: 10,
                    }}
                  >
                    {t('Notifications', '通知')}
                  </Link>
                  <Link
                    href="/my-qr"
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: 'block', padding: '10px 8px', borderRadius: 2,
                      fontSize: 14, fontWeight: isActive('/my-qr') ? 700 : 500,
                      textDecoration: 'none', minHeight: 42,
                      color: isActive('/my-qr') ? '#070707' : '#5F625F',
                      backgroundColor: isActive('/my-qr') ? '#B8FF00' : 'transparent',
                      borderLeft: isActive('/my-qr') ? '3px solid #070707' : '3px solid transparent',
                      paddingLeft: 10,
                    }}
                  >
                    {t('My QR Code', '我的二维码')}
                  </Link>
                </>
              )}

              {/* ── ADMIN ── */}
              {showAdmin && (
                <>
                  <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#5F625F', padding: '12px 8px 6px', marginTop: 4, borderTop: '1px solid #D8D8CE', fontFamily: 'var(--font-mono, monospace)' }}>
                    Admin
                  </p>
                  <Link
                    href="/admin-dashboard"
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 8px', borderRadius: 2,
                      fontSize: 14, fontWeight: 600, textDecoration: 'none', minHeight: 42,
                      color: isActive('/admin-dashboard') ? '#070707' : '#5F625F',
                      backgroundColor: isActive('/admin-dashboard') ? '#B8FF00' : 'transparent',
                      borderLeft: isActive('/admin-dashboard') ? '3px solid #070707' : '3px solid transparent',
                      paddingLeft: 10,
                    }}
                  >
                    <Settings size={14} />
                    {t('Admin Dashboard', '管理后台')}
                  </Link>
                  <Link
                    href="/attendance-dashboard"
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 8px', borderRadius: 2,
                      fontSize: 14, fontWeight: 600, textDecoration: 'none', minHeight: 42,
                      color: isActive('/attendance-dashboard') ? '#070707' : '#5F625F',
                      backgroundColor: isActive('/attendance-dashboard') ? '#B8FF00' : 'transparent',
                      borderLeft: isActive('/attendance-dashboard') ? '3px solid #070707' : '3px solid transparent',
                      paddingLeft: 10,
                    }}
                  >
                    {t('Attendance Dashboard', '出勤仪表板')}
                  </Link>
                  <Link
                    href="/leader-scanner"
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 8px', borderRadius: 2,
                      fontSize: 14, fontWeight: 600, textDecoration: 'none', minHeight: 42,
                      color: isActive('/leader-scanner') ? '#070707' : '#5F625F',
                      backgroundColor: isActive('/leader-scanner') ? '#B8FF00' : 'transparent',
                      borderLeft: isActive('/leader-scanner') ? '3px solid #070707' : '3px solid transparent',
                      paddingLeft: 10,
                    }}
                  >
                    {t('Leader Scanner', '领队扫描仪')}
                  </Link>
                  <button
                    onClick={async () => { setMobileOpen(false); await signOut(); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 8px', borderRadius: 2, fontSize: 14, fontWeight: 500, border: 'none', background: 'transparent', cursor: 'pointer', minHeight: 42, color: '#FF3B1F', textAlign: 'left', paddingLeft: 10 }}
                  >
                    <LogOut size={14} />
                    {t('Sign Out', '退出登录')}
                  </button>
                </>
              )}

              {/* ── NOT LOGGED IN ── */}
              {!isLoggedIn && (
                <div style={{ marginTop: 4, paddingTop: 8, borderTop: '1px solid #D8D8CE' }}>
                  <button
                    onClick={() => { setMobileOpen(false); onLoginClick?.(); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 8px', borderRadius: 2, fontSize: 14, fontWeight: 500, border: 'none', background: 'transparent', cursor: 'pointer', minHeight: 42, color: '#5F625F', textAlign: 'left', paddingLeft: 10 }}
                  >
                    <LogIn size={14} />
                    {t('Login', '登录')}
                  </button>
                  <Link
                    href="/sign-up-login"
                    onClick={() => setMobileOpen(false)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 8px', borderRadius: 2, fontSize: 14, fontWeight: 700, textDecoration: 'none', minHeight: 42, color: '#070707', backgroundColor: '#B8FF00', paddingLeft: 10 }}
                  >
                    <UserPlus size={14} />
                    {t('Create Account', '创建账户')}
                  </Link>
                </div>
              )}

              {/* ── SIGN OUT for non-admin logged-in users ── */}
              {isLoggedIn && !showAdmin && (
                <button
                  onClick={async () => { setMobileOpen(false); await signOut(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 8px', borderRadius: 2, fontSize: 14, fontWeight: 500, border: 'none', background: 'transparent', cursor: 'pointer', minHeight: 42, color: '#FF3B1F', textAlign: 'left', marginTop: 4, borderTop: '1px solid #D8D8CE', paddingLeft: 10 }}
                >
                  <LogOut size={14} />
                  {t('Sign Out', '退出登录')}
                </button>
              )}

              {/* ── Language toggle ── */}
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #D8D8CE', display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setLanguage('EN')}
                  style={{
                    flex: 1, padding: '8px', borderRadius: 2, fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    border: '1.5px solid', cursor: 'pointer',
                    fontFamily: 'var(--font-mono, monospace)',
                    backgroundColor: language === 'EN' ? '#B8FF00' : 'transparent',
                    borderColor: language === 'EN' ? '#B8FF00' : '#D8D8CE',
                    color: '#070707',
                  }}
                >EN</button>
                <button
                  onClick={() => setLanguage('ZH')}
                  style={{
                    flex: 1, padding: '8px', borderRadius: 2, fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    border: '1.5px solid', cursor: 'pointer',
                    fontFamily: 'var(--font-mono, monospace)',
                    backgroundColor: language === 'ZH' ? '#B8FF00' : 'transparent',
                    borderColor: language === 'ZH' ? '#B8FF00' : '#D8D8CE',
                    color: '#070707',
                  }}
                >中文</button>
              </div>
            </div>
          </div>
        </div>,
        portalRoot
      )
    : null;

  return (
    <>
      <header
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          height: 56,
          backgroundColor: scrolled ? 'rgba(247,247,242,0.98)' : 'rgba(247,247,242,0.95)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: scrolled ? '1px solid #111111' : '1px solid #D8D8CE',
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
        }}
      >
        <div
          style={{
            maxWidth: '1536px', margin: '0 auto',
            padding: '0 24px',
            height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 16,
          }}
        >
          {/* ── Logo ── */}
          <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', flexShrink: 0 }}>
            <AppLogo size={24} />
            <span style={{ fontFamily: 'var(--font-heading, "Arial Black", sans-serif)', fontWeight: 900, fontSize: 15, color: '#070707', letterSpacing: '-0.01em', textTransform: 'uppercase' }}>
              R3SET
            </span>
            <span style={{ fontSize: 13, fontWeight: 400, color: '#5F625F', letterSpacing: '0.02em' }}>
              Running
            </span>
          </Link>

          {/* ── Desktop Nav ── */}
          <nav style={{ display: 'none', alignItems: 'center', gap: 2 }} className="hidden lg:flex">
            {primaryNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                style={{
                  padding: '6px 12px',
                  borderRadius: 2,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  fontFamily: 'var(--font-mono, monospace)',
                  color: isActive(item.href) ? '#070707' : '#5F625F',
                  backgroundColor: isActive(item.href) ? '#B8FF00' : 'transparent',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (!isActive(item.href)) {
                    (e.currentTarget as HTMLElement).style.color = '#070707';
                    (e.currentTarget as HTMLElement).style.backgroundColor = '#EDFFD0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item.href)) {
                    (e.currentTarget as HTMLElement).style.color = '#5F625F';
                    (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  }
                }}
              >
                {t(item.labelEN, item.labelZH)}
              </Link>
            ))}
          </nav>

          {/* ── Right side ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>

            {/* Language toggle — desktop */}
            <div className="hidden lg:flex" style={{ gap: 2 }}>
              {(['EN', 'ZH'] as const).map((lang) => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang)}
                  style={{
                    padding: '4px 8px',
                    borderRadius: 2,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    border: '1.5px solid',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-mono, monospace)',
                    backgroundColor: language === lang ? '#B8FF00' : 'transparent',
                    borderColor: language === lang ? '#B8FF00' : '#D8D8CE',
                    color: '#070707',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {lang === 'EN' ? 'EN' : '中'}
                </button>
              ))}
            </div>

            {/* Auth buttons — desktop */}
            <div className="hidden lg:flex" style={{ alignItems: 'center', gap: 6 }}>
              {!authResolved ? (
                // Stable placeholder during auth loading — prevents SSR/client mismatch
                <div style={{ width: 80, height: 30 }} />
              ) : isLoggedIn ? (
                <>
                  {/* Notification Bell */}
                  <NotificationBell isZH={isZH} />

                  {showAdmin && (
                    <Link
                      href="/admin-dashboard"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '5px 10px', borderRadius: 2,
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', textDecoration: 'none',
                        fontFamily: 'var(--font-mono, monospace)',
                        border: '1.5px solid #111111',
                        color: '#070707', backgroundColor: 'transparent',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.backgroundColor = '#B8FF00';
                        (e.currentTarget as HTMLElement).style.borderColor = '#B8FF00';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                        (e.currentTarget as HTMLElement).style.borderColor = '#111111';
                      }}
                    >
                      <Settings size={12} />
                      {t('Admin', '管理')}
                    </Link>
                  )}
                  {showAdmin && (
                    <Link
                      href="/attendance-dashboard"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '5px 10px', borderRadius: 2,
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', textDecoration: 'none',
                        fontFamily: 'var(--font-mono, monospace)',
                        border: '1.5px solid #D8D8CE',
                        color: '#5F625F', backgroundColor: 'transparent',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLElement).style.borderColor = '#111111';
                        (e.currentTarget as HTMLElement).style.color = '#070707';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.borderColor = '#D8D8CE';
                        (e.currentTarget as HTMLElement).style.color = '#5F625F';
                      }}
                    >
                      {t('Attendance', '出勤')}
                    </Link>
                  )}
                  <Link
                    href="/my-dashboard"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 10px', borderRadius: 2,
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                      textTransform: 'uppercase', textDecoration: 'none',
                      fontFamily: 'var(--font-mono, monospace)',
                      border: '1.5px solid #D8D8CE',
                      color: '#5F625F', backgroundColor: 'transparent',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = '#111111';
                      (e.currentTarget as HTMLElement).style.color = '#070707';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = '#D8D8CE';
                      (e.currentTarget as HTMLElement).style.color = '#5F625F';
                    }}
                  >
                    <User size={12} />
                    {t('Dashboard', '仪表板')}
                  </Link>
                  <button
                    onClick={() => signOut()}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 10px', borderRadius: 2,
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      fontFamily: 'var(--font-mono, monospace)',
                      border: '1.5px solid #D8D8CE',
                      color: '#5F625F', backgroundColor: 'transparent',
                      cursor: 'pointer', transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = '#FF3B1F';
                      (e.currentTarget as HTMLElement).style.color = '#FF3B1F';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = '#D8D8CE';
                      (e.currentTarget as HTMLElement).style.color = '#5F625F';
                    }}
                  >
                    <LogOut size={12} />
                    {t('Out', '退出')}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={onLoginClick}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 10px', borderRadius: 2,
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      fontFamily: 'var(--font-mono, monospace)',
                      border: '1.5px solid #D8D8CE',
                      color: '#5F625F', backgroundColor: 'transparent',
                      cursor: 'pointer', transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = '#111111';
                      (e.currentTarget as HTMLElement).style.color = '#070707';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = '#D8D8CE';
                      (e.currentTarget as HTMLElement).style.color = '#5F625F';
                    }}
                  >
                    <LogIn size={12} />
                    {t('Login', '登录')}
                  </button>
                  <Link
                    href="/sign-up-login"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 10px', borderRadius: 2,
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                      textTransform: 'uppercase', textDecoration: 'none',
                      fontFamily: 'var(--font-mono, monospace)',
                      border: '1.5px solid #070707',
                      color: '#070707', backgroundColor: 'transparent',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = '#B8FF00';
                      (e.currentTarget as HTMLElement).style.borderColor = '#B8FF00';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                      (e.currentTarget as HTMLElement).style.borderColor = '#070707';
                    }}
                  >
                    <UserPlus size={12} />
                    {t('Join', '加入')}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden"
              onClick={() => setMobileOpen((v) => !v)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 36, height: 36, borderRadius: 2,
                border: '1.5px solid #D8D8CE',
                backgroundColor: mobileOpen ? '#B8FF00' : 'transparent',
                cursor: 'pointer', transition: 'all 0.15s ease',
              }}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={16} color="#070707" /> : <Menu size={16} color="#070707" />}
            </button>
          </div>
        </div>
      </header>

      {/* Spacer */}
      <div style={{ height: 56 }} />

      {mobileDrawer}
    </>
  );
}